// Import necessary components and libraries
import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { Modal } from "react-bootstrap";
import { FaEdit, FaTrash, FaClock, FaCodeBranch, FaBriefcaseMedical, FaPlus, FaKey, FaUserSecret } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getUserFromLocalStorage } from "utilities/user.js";
import { useCountry } from "../context/CountryContext.js";
import { usePermissions } from "../utilities/usePermissions.js";
import {
  fetchHealthcareVendors,
  updateHealthcareProfile,
  deleteHealthcareVendor,
} from "../actions/healthcareProfileActions.js";
import EditHealthcareForm from "./EditHealthcareForm.js";
import { toast } from "react-toastify";
import OpenHoursManager from "../components/OpenHours/OpenHoursManager";
import SendVendorCredentialsAction from "../components/VendorCredentials/SendVendorCredentialsAction";
import SignInAsVendorButton from "../components/SignInAsVendorButton";
import {
  apiUpdateHealthcareOpenHours,
  apiGetHealthcareOpenStatus,
  apiCreateHealthcareOutlet,
  apiUpdateHealthcareOutlet,
  apiDeleteHealthcareOutlet,
  apiToggleHealthcareOutletActivation,
  apiGetHealthcareOutletHours,
  apiUpdateHealthcareOutletHours,
  apiToggleHealthcareAIShoppable,
} from "../api/index";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { useGoogleMaps } from "../GoogleMapsContext";
import LocationAutocomplete from "./LocationAutoComplete.js";

// Main component
const Healthcares = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isSuperAdmin: hasSuperAdminCapability } = useCountry();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const user = getUserFromLocalStorage();
  const adminId = user?.result?._id;
  const adminRole = String(
    user?.result?.user_role || user?.result?.role || ""
  ).toUpperCase();
  const isSuperAdmin =
    hasSuperAdminCapability ||
    adminRole === "SUPERADMIN" ||
    adminRole === "SUPERUSER";
  const canManageHealthcare =
    isSuperAdmin || (permissionsLoaded && can("healthcare:manage"));
  const canCreateHealthcare =
    canManageHealthcare || (permissionsLoaded && can("healthcare:create"));
  const canUpdateHealthcare =
    canManageHealthcare || (permissionsLoaded && can("healthcare:update"));
  const canDeleteHealthcare =
    canManageHealthcare || (permissionsLoaded && can("healthcare:delete"));
  const canApproveHealthcare =
    canManageHealthcare || (permissionsLoaded && can("healthcare:approve"));
  const canViewDirectory =
    user?.result?.userType === "LUSUKU STAFF" &&
    (isSuperAdmin || !permissionsLoaded || can("healthcare:read"));
  const { mapsApiLoaded } = useGoogleMaps();
  const { loading, healthcareVendors, error } = useSelector(
    (state) => state.healthcareProfile
  );
  const [editingHealthcare, setEditingHealthcare] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [healthcareToDelete, setHealthcareToDelete] = useState(null);
  const [showAttachOutletModal, setShowAttachOutletModal] = useState(false);
  const [outletDetails, setOutletDetails] = useState({
    name: "",
    location: "",
    latitude: null,
    longitude: null,
    deliveryZoneRadius: 5,
  });
  const [selectedHealthcare, setSelectedHealthcare] = useState(null);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [showEditOutletModal, setShowEditOutletModal] = useState(false);
  const [showDeleteOutletModal, setShowDeleteOutletModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOutletConfirmModal, setShowOutletConfirmModal] = useState(false);
  const [outletToConfirm, setOutletToConfirm] = useState(null);
  const [healthcareForConfirm, setHealthcareForConfirm] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [toggleOutletLoading, setToggleOutletLoading] = useState({});
  const [aiToggleLoading, setAiToggleLoading] = useState({});
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalTarget, setAiModalTarget] = useState(null); // { id, name, currentValue }
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [selectedHealthcareForHours, setSelectedHealthcareForHours] = useState(null);
  const [healthcareHours, setHealthcareHours] = useState(null);
  const [isLoadingHours, setIsLoadingHours] = useState(false);
  const [isSavingHours, setIsSavingHours] = useState(false);
  const [showOutletHoursModal, setShowOutletHoursModal] = useState(false);
  const [selectedOutletForHours, setSelectedOutletForHours] = useState(null); // { outlet, healthcare }
  const [outletHours, setOutletHours] = useState(null);
  const [isLoadingOutletHours, setIsLoadingOutletHours] = useState(false);
  const [isSavingOutletHours, setIsSavingOutletHours] = useState(false);

  function useCustomPlacesAutocomplete(mapsApiLoaded) {
    const options = {
      requestOptions: {
        componentRestrictions: { country: "ug" },
        cacheTimeout: 10 * 60 * 60 * 24,
        debounce: 100,
        location: { lat: () => 0.347596, lng: () => 32.58252 },
        radius: 200 * 1000,
        origin: { lat: 0.3148136, lng: 32.5788721 },
        strictBounds: true,
        types: ["establishment"],
      },
      debounce: 100,
    };

    const {
      value,
      ready,
      suggestions: { data, loading: placesLoading, status: suggestionStatus } = { data: [] },
      setValue,
      clearSuggestions,
    } = usePlacesAutocomplete(options);

    useEffect(() => {
      if (mapsApiLoaded) {
        setValue("");
      }
    }, [mapsApiLoaded]);

    return {
      value,
      ready,
      data,
      placesLoading,
      suggestionStatus,
      setValue,
      clearSuggestions,
    };
  }

  const { value, ready, data, placesLoading, suggestionStatus, setValue, clearSuggestions } =
    useCustomPlacesAutocomplete(mapsApiLoaded);

  useEffect(() => {
    dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
  }, [dispatch, adminId, canViewDirectory]);

  const handleRetry = () => {
    dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
  };

  const handleDeleteHealthcare = (id) => {
    if (canDeleteHealthcare) {
      setHealthcareToDelete(id);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteHealthcare = async () => {
    if (healthcareToDelete && canDeleteHealthcare) {
      setIsSubmitting(true);
      try {
        await dispatch(deleteHealthcareVendor(healthcareToDelete));
        setShowDeleteModal(false);
        setHealthcareToDelete(null);
      } catch (_error) {
        // The action reports the API error and leaves the confirmation open.
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEditClick = (healthcare) => {
    if (!canUpdateHealthcare) return;
    setEditingHealthcare(healthcare);
  };

  const handleSave = async (updatedHealthcare) => {
    if (!canUpdateHealthcare) return;
    const ownerId = editingHealthcare?.owner || editingHealthcare?._id;
    if (!ownerId) {
      toast.error("Unable to save: Owner ID not found");
      return;
    }
    try {
      await dispatch(updateHealthcareProfile(ownerId, updatedHealthcare));
      toast.success("Healthcare vendor updated successfully");
      setEditingHealthcare(null);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (_error) {
      // The action reports the API error; keep the form open so it can be corrected.
    }
  };

  const handleCancel = () => {
    setEditingHealthcare(null);
  };

  const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal);

  const handleAttachOutletClick = (healthcare) => {
    if (!canCreateHealthcare) return;
    setSelectedHealthcare(healthcare);
    setShowAttachOutletModal(true);
  };

  const handleOutletDetailsChange = (e) => {
    const { name, value } = e.target;
    setOutletDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const handleLocationChange = (locationData) => {
    setOutletDetails((prev) => ({
      ...prev,
      location: locationData.address,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
    }));
  };

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({
        address,
        componentRestrictions: { country: "ug" },
        region: "ug",
      });

      if (results && results.length > 0) {
        const { lat, lng } = await getLatLng(results[0]);

        setOutletDetails((prev) => ({
          ...prev,
          location: address,
          latitude: lat,
          longitude: lng,
        }));
      } else {
        throw new Error("No results found");
      }
    } catch (error) {
      console.error("Geocoding error details:", error);
      toast.error(
        "Error getting location coordinates. Please try selecting a different address."
      );
    }
  };

  const handleAttachOutlet = async () => {
    if (!canCreateHealthcare) return;
    try {
      if (!selectedHealthcare) {
        toast.error("Please select a healthcare vendor first");
        return;
      }

      if (
        !outletDetails.name ||
        !outletDetails.location ||
        !outletDetails.latitude ||
        !outletDetails.longitude ||
        !outletDetails.deliveryZoneRadius
      ) {
        toast.error("Please fill in all required fields including location");
        return;
      }

      setIsSubmitting(true);

      const payload = {
        name: outletDetails.name,
        address: outletDetails.location,
        latitude: outletDetails.latitude,
        longitude: outletDetails.longitude,
        deliveryZoneRadius: Number(outletDetails.deliveryZoneRadius),
      };

      await apiCreateHealthcareOutlet(selectedHealthcare._id, payload);

      toast.success("Outlet attached successfully!");

      setOutletDetails({
        name: "",
        location: "",
        latitude: null,
        longitude: null,
        deliveryZoneRadius: 5,
      });

      setValue("");
      clearSuggestions();

      setShowAttachOutletModal(false);

      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      console.error("Error attaching outlet:", error);
      toast.error(error.message || "Error attaching outlet");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAttachOutletModal = () => {
    if (showAttachOutletModal) {
      setOutletDetails({
        name: "",
        location: "",
        latitude: null,
        longitude: null,
        deliveryZoneRadius: 5,
      });
      setValue("");
      clearSuggestions();
    }
    setShowAttachOutletModal(!showAttachOutletModal);
  };

  const openEditOutletModal = (outlet, healthcare) => {
    if (!canUpdateHealthcare) return;
    setSelectedHealthcare(healthcare);
    setEditingOutlet({
      _id: outlet._id,
      name: outlet.name || "",
      address: outlet.address || "",
      deliveryZoneRadius: outlet.deliveryZoneRadius || 5,
    });
    setShowEditOutletModal(true);
  };

  const handleDeleteOutlet = (outlet, healthcare) => {
    if (!canDeleteHealthcare) return;
    setOutletToConfirm(outlet);
    setHealthcareForConfirm(healthcare);
    setShowOutletConfirmModal(true);
  };

  const confirmDeleteOutlet = async () => {
    if (outletToConfirm && healthcareForConfirm && canDeleteHealthcare) {
      setIsSubmitting(true);
      try {
        await apiDeleteHealthcareOutlet(healthcareForConfirm._id, outletToConfirm._id);
        toast.success("Outlet deactivated successfully!");
        setShowOutletConfirmModal(false);
        setOutletToConfirm(null);
        setHealthcareForConfirm(null);
        dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
      } catch (error) {
        toast.error("Failed to deactivate outlet");
        console.error("Error deactivating outlet:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const saveOutletChanges = async () => {
    if (!editingOutlet || !selectedHealthcare || !canUpdateHealthcare) return;

    setIsSubmitting(true);
    try {
      const updatedData = {
        name: editingOutlet.name,
        address: editingOutlet.address,
        deliveryZoneRadius: editingOutlet.deliveryZoneRadius,
      };
      await apiUpdateHealthcareOutlet(selectedHealthcare._id, editingOutlet._id, updatedData);
      toast.success("Outlet updated successfully!");
      setShowEditOutletModal(false);
      setEditingOutlet(null);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      toast.error("Failed to update outlet");
      console.error("Error updating outlet:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleAIShoppable = (vendorId, vendorName, currentValue) => {
    if (!canManageHealthcare) return;
    setAiModalTarget({ id: vendorId, name: vendorName, currentValue });
    setShowAIModal(true);
  };

  const confirmToggleAI = async () => {
    if (!aiModalTarget || !canManageHealthcare) return;
    const { id, currentValue } = aiModalTarget;
    const next = !currentValue;
    setShowAIModal(false);
    setAiToggleLoading((prev) => ({ ...prev, [id]: true }));
    try {
      await apiToggleHealthcareAIShoppable(id, next);
      toast.success(`AI Commerce ${next ? 'enabled' : 'disabled'} successfully.`);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      toast.error('Failed to update AI-shoppable setting');
    } finally {
      setAiToggleLoading((prev) => ({ ...prev, [id]: false }));
      setAiModalTarget(null);
    }
  };

  const handleToggleOutletActive = (outletId, currentStatus, outletName) => {
    if (!canApproveHealthcare) return;

    const action = currentStatus ? "deactivate" : "activate";
    setOutletToConfirm({ _id: outletId, name: outletName, isActive: currentStatus });
    setConfirmAction(action);
    setShowOutletConfirmModal(true);
  };

  const confirmToggleOutlet = async () => {
    if (!outletToConfirm || !confirmAction || !canApproveHealthcare) return;

    setIsSubmitting(true);
    setToggleOutletLoading((prev) => ({ ...prev, [outletToConfirm._id]: true }));
    try {
      await apiToggleHealthcareOutletActivation(outletToConfirm._id, !outletToConfirm.isActive);
      toast.success(`Outlet ${confirmAction}d successfully!`);
      setShowOutletConfirmModal(false);
      setOutletToConfirm(null);
      setConfirmAction(null);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      toast.error(`Failed to ${confirmAction} outlet`);
      console.error(`Error ${confirmAction}ing outlet:`, error);
    } finally {
      setIsSubmitting(false);
      setToggleOutletLoading((prev) => ({ ...prev, [outletToConfirm._id]: false }));
    }
  };

  const handleManageOutletHours = async (outlet, healthcare) => {
    if (!canUpdateHealthcare) return;
    setSelectedOutletForHours({ outlet, healthcare });
    setShowOutletHoursModal(true);
    setIsLoadingOutletHours(true);
    try {
      const response = await apiGetHealthcareOutletHours(outlet._id);
      setOutletHours(response.data.openHours || null);
    } catch (error) {
      console.error("Error fetching outlet hours:", error);
      toast.error("Failed to load outlet hours");
      setOutletHours(null);
    } finally {
      setIsLoadingOutletHours(false);
    }
  };

  const handleSaveOutletHours = async (hoursData) => {
    if (!selectedOutletForHours || !canUpdateHealthcare) return;
    setIsSavingOutletHours(true);
    try {
      await apiUpdateHealthcareOutletHours(selectedOutletForHours.outlet._id, {
        openHours: hoursData,
      });
      toast.success(`Hours updated for ${selectedOutletForHours.outlet.name}!`);
      setShowOutletHoursModal(false);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      console.error("Error saving outlet hours:", error);
      toast.error("Failed to update outlet hours");
    } finally {
      setIsSavingOutletHours(false);
    }
  };

  const handleManageHours = async (healthcare) => {
    if (!canUpdateHealthcare) return;
    setSelectedHealthcareForHours(healthcare);
    setShowHoursModal(true);
    setIsLoadingHours(true);

    try {
      const response = await apiGetHealthcareOpenStatus(healthcare._id);
      setHealthcareHours(response.data.openHours || null);
    } catch (error) {
      console.error("Error fetching healthcare hours:", error);
      toast.error("Failed to load healthcare hours");
      setHealthcareHours(null);
    } finally {
      setIsLoadingHours(false);
    }
  };

  const handleSaveHealthcareHours = async (hoursData, timezone) => {
    if (!selectedHealthcareForHours || !canUpdateHealthcare) return;

    setIsSavingHours(true);
    try {
      await apiUpdateHealthcareOpenHours(selectedHealthcareForHours._id, {
        openHours: hoursData,
        ...(timezone ? { timezone } : {}),
      });

      toast.success(
        `Business hours updated for ${selectedHealthcareForHours.name}!`
      );
      setShowHoursModal(false);
      dispatch(fetchHealthcareVendors(adminId, canViewDirectory));
    } catch (error) {
      console.error("Error saving healthcare hours:", error);
      toast.error("Failed to update business hours");
    } finally {
      setIsSavingHours(false);
    }
  };

  const pbKpi = {
    wrapper: {
      display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap",
    },
    tile: {
      flex: "1 1 160px", background: "#fff", borderRadius: "6px",
      padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      borderTop: "3px solid #118DFF", minWidth: "140px",
    },
    tileValue: {
      fontSize: "2rem", fontWeight: 700, color: "#1B2A3B", lineHeight: 1,
    },
    tileLabel: {
      fontSize: "0.72rem", color: "#6B7A99", textTransform: "uppercase",
      letterSpacing: "0.07em", marginTop: "6px", fontWeight: 600,
    },
  };

  const totalOutlets = healthcareVendors.reduce(
    (s, h) => s + (h.outlets ? h.outlets.length : 0), 0
  );
  const aiEnabledCount = healthcareVendors.filter((h) => h.isAIShoppable).length;
  const hoursConfigured = healthcareVendors.filter((h) => h.openHours).length;

  const pbTh = {
    padding: "10px 14px", fontSize: "0.7rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.08em", color: "#6B7A99",
    borderBottom: "2px solid #118DFF", background: "#FAFBFC",
    whiteSpace: "nowrap",
  };
  const pbTd = {
    padding: "12px 14px", verticalAlign: "middle", borderBottom: "1px solid #F0F2F5",
    fontSize: "0.85rem", color: "#1B2A3B",
  };

  const PbActionBtn = ({ onClick, disabled, title, danger, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
        color: disabled ? "#C8D0DC" : danger ? "#C00000" : "#5C6B7A",
        padding: "5px 7px", borderRadius: "4px", display: "inline-flex",
        alignItems: "center", justifyContent: "center", transition: "background 0.15s",
        fontSize: "13px",
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = danger ? "#FDE7E9" : "#EBF4FF"; e.currentTarget.style.color = disabled ? "#C8D0DC" : danger ? "#C00000" : "#118DFF"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = disabled ? "#C8D0DC" : danger ? "#C00000" : "#5C6B7A"; }}
    >
      {children}
    </button>
  );

  let content;

  if (loading) {
    content = (
      <div style={{ padding: "20px 0" }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{
            height: "64px", background: "linear-gradient(90deg,#F0F2F5 25%,#E4E8EF 50%,#F0F2F5 75%)",
            backgroundSize: "200% 100%", borderRadius: "6px", marginBottom: "8px",
            animation: "pbShimmer 1.4s infinite",
          }} />
        ))}
        <style>{`@keyframes pbShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      </div>
    );
  } else if (error) {
    content = (
      <div style={{
        display: "flex", alignItems: "center", gap: "12px", padding: "20px",
        background: "#FDE7E9", borderRadius: "6px", border: "1px solid #F5C6CB",
      }}>
        <span style={{ color: "#C00000", fontWeight: 600, fontSize: "0.88rem" }}>{error}</span>
        <button
          onClick={handleRetry}
          style={{
            background: "#118DFF", color: "#fff", border: "none", borderRadius: "4px",
            padding: "6px 14px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
          }}
        >
          ⟳ Retry
        </button>
      </div>
    );
  } else {
    content = (
      <>
        {/* ── KPI Tiles ── */}
        <div style={pbKpi.wrapper}>
          <div style={pbKpi.tile}>
            <div style={pbKpi.tileValue}>{healthcareVendors.length}</div>
            <div style={pbKpi.tileLabel}>Total Vendors</div>
          </div>
          <div style={{ ...pbKpi.tile, borderTopColor: "#107C41" }}>
            <div style={{ ...pbKpi.tileValue, color: "#107C41" }}>{totalOutlets}</div>
            <div style={pbKpi.tileLabel}>Total Outlets</div>
          </div>
          <div style={{ ...pbKpi.tile, borderTopColor: "#8764B8" }}>
            <div style={{ ...pbKpi.tileValue, color: "#8764B8" }}>{aiEnabledCount}</div>
            <div style={pbKpi.tileLabel}>AI Enabled</div>
          </div>
          <div style={{ ...pbKpi.tile, borderTopColor: "#F7C948" }}>
            <div style={{ ...pbKpi.tileValue, color: "#D4930A" }}>{hoursConfigured}</div>
            <div style={pbKpi.tileLabel}>Hours Set</div>
          </div>
        </div>

        {/* ── Data Table ── */}
        <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid #E8ECF2" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
            <thead>
              <tr>
                <th style={{ ...pbTh, width: "36px" }}>#</th>
                <th style={pbTh}>Name</th>
                <th style={{ ...pbTh, textAlign: "center" }}>Banner / Logo</th>
                <th style={{ ...pbTh, width: "18%" }}>Location</th>
                <th style={pbTh}>Outlets</th>
                <th style={{ ...pbTh, textAlign: "center" }}>Hours</th>
                <th style={{ ...pbTh, textAlign: "center" }}>AI</th>
                <th style={pbTh}>ID</th>
                <th style={{ ...pbTh, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {healthcareVendors.length === 0 && (
                <tr>
                  <td colSpan="9" style={{ padding: "44px 20px", textAlign: "center" }}>
                    <FaBriefcaseMedical style={{ fontSize: 30, color: "#B8C4D4", marginBottom: 10 }} />
                    <div style={{ fontWeight: 700, color: "#3D4F6B" }}>
                      No healthcare vendors in this market
                    </div>
                    <div style={{ color: "#7D8DA6", fontSize: "0.82rem", marginTop: 4 }}>
                      Create a healthcare vendor account to start managing its profile and outlets.
                    </div>
                  </td>
                </tr>
              )}
              {healthcareVendors.map((healthcare, index) => (
                <tr
                  key={healthcare._id || healthcare.healthcareId || `healthcare-${index}`}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#F3F9FF")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                  style={{ background: "#fff", transition: "background 0.12s" }}
                >
                  {/* # */}
                  <td style={{ ...pbTd, color: "#9AACBF", fontWeight: 700 }}>{index + 1}</td>

                  {/* Name */}
                  <td style={{ ...pbTd, fontWeight: 600, color: "#1B2A3B" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {healthcare.imageUrl ? (
                        <img
                          src={healthcare.imageUrl}
                          alt={healthcare.name}
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: "2px solid #E8ECF2", flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%", background: "#EBF4FF",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <FaBriefcaseMedical style={{ fontSize: 13, color: "#118DFF" }} />
                        </div>
                      )}
                      <div>
                        <div>{healthcare.name}</div>
                        <span style={{
                          display: "inline-block", marginTop: 3, padding: "1px 7px",
                          borderRadius: 10, fontSize: "0.6rem", fontWeight: 700,
                          ...(healthcare.isActive !== false
                            ? { background: "#E6F4EA", color: "#107C41" }
                            : { background: "#FDE7E9", color: "#A50021" }),
                        }}>
                          {healthcare.isActive !== false ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Banner / Logo */}
                  <td style={{ ...pbTd, textAlign: "center" }}>
                    {healthcare.bannerUrl ? (
                      <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        <div style={{
                          width: 80, height: 52, borderRadius: 4, overflow: "hidden",
                          border: "1px solid #E8ECF2", boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                          position: "relative",
                        }}>
                          <img src={healthcare.bannerUrl} alt="banner" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <span style={{
                            position: "absolute", top: 3, left: 3, background: "#118DFF",
                            color: "#fff", fontSize: "0.5rem", fontWeight: 700,
                            padding: "1px 5px", borderRadius: 999, textTransform: "uppercase",
                          }}>Banner</span>
                        </div>
                        <span style={{ fontSize: "0.65rem", color: "#107C41", fontWeight: 600 }}>✓ Set</span>
                      </div>
                    ) : (
                      <div style={{
                        width: 80, height: 52, borderRadius: 4, border: "1.5px dashed #CBD5E0",
                        background: "#FAFBFC", display: "inline-flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: 2, margin: "0 auto",
                      }}>
                        <span style={{ fontSize: "1.1rem" }}>🖼️</span>
                        <span style={{ fontSize: "0.55rem", color: "#9AACBF", fontWeight: 600 }}>No banner</span>
                      </div>
                    )}
                  </td>

                  {/* Location */}
                  <td style={{ ...pbTd, maxWidth: 180 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                      <i className="fas fa-map-marker-alt" style={{ color: "#118DFF", marginTop: 2, flexShrink: 0, fontSize: 11 }}></i>
                      <span style={{ fontSize: "0.82rem", color: "#3D4F6B", lineHeight: 1.4 }}>{healthcare.address}</span>
                    </div>
                  </td>

                  {/* Outlets */}
                  <td style={{ ...pbTd, minWidth: 220 }}>
                    {healthcare.outlets && healthcare.outlets.length > 0 ? (
                      <div>
                        <div style={{ marginBottom: 8 }}>
                          <span style={{
                            background: "#EBF4FF", color: "#118DFF", padding: "2px 10px",
                            borderRadius: 12, fontSize: "0.72rem", fontWeight: 700,
                            border: "1px solid #BDD9FA",
                          }}>
                            {healthcare.outlets.length} {healthcare.outlets.length === 1 ? "Outlet" : "Outlets"}
                          </span>
                        </div>
                        <div style={{ maxHeight: 160, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                          {healthcare.outlets.map((outlet, idx) => (
                            <div
                              key={outlet._id || `outlet-${idx}`}
                              style={{
                                background: "#FAFBFC", borderLeft: "3px solid #118DFF",
                                borderRadius: "0 4px 4px 0", padding: "8px 10px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                                    <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1B2A3B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 110 }}>{outlet.name}</span>
                                    <span style={{
                                      fontSize: "0.58rem", fontWeight: 700, padding: "1px 6px",
                                      borderRadius: 10, textTransform: "uppercase", flexShrink: 0,
                                      ...(outlet.isActive !== false
                                        ? { background: "#E6F4EA", color: "#107C41", border: "1px solid #B7DFC5" }
                                        : { background: "#FDE7E9", color: "#A50021", border: "1px solid #F5C6CB" })
                                    }}>
                                      {outlet.isActive !== false ? "Active" : "Inactive"}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: "0.75rem", color: "#6B7A99", display: "flex", alignItems: "center", gap: 4 }}>
                                    <i className="fas fa-map-marker-alt" style={{ color: "#A50021", fontSize: 9 }}></i>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120 }}>{outlet.address}</span>
                                  </div>
                                  <div style={{ fontSize: "0.72rem", color: "#9AACBF", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
                                    <i className="fas fa-route" style={{ color: "#118DFF", fontSize: 9 }}></i>
                                    Radius: {outlet.deliveryZoneRadius} km
                                  </div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0, marginLeft: 6 }}>
                                  <PbActionBtn
                                    onClick={() => handleToggleOutletActive(outlet._id, outlet.isActive !== false, outlet.name)}
                                    disabled={!canApproveHealthcare || toggleOutletLoading[outlet._id]}
                                    title={outlet.isActive !== false ? "Deactivate outlet" : "Activate outlet"}
                                  >
                                    <span style={{ fontSize: 10, fontWeight: 700, color: outlet.isActive !== false ? "#107C41" : "#A50021" }}>
                                      {outlet.isActive !== false ? "●" : "○"}
                                    </span>
                                  </PbActionBtn>
                                  <PbActionBtn onClick={() => openEditOutletModal(outlet, healthcare)} disabled={!canUpdateHealthcare} title="Edit outlet">
                                    <FaEdit />
                                  </PbActionBtn>
                                  <PbActionBtn onClick={() => handleManageOutletHours(outlet, healthcare)} disabled={!canUpdateHealthcare} title="Outlet hours">
                                    <FaClock />
                                  </PbActionBtn>
                                  <PbActionBtn onClick={() => handleDeleteOutlet(outlet, healthcare)} disabled={!canDeleteHealthcare} title="Remove outlet" danger>
                                    <FaTrash />
                                  </PbActionBtn>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#B0BCCF", fontSize: "0.8rem", fontStyle: "italic" }}>
                        <i className="fas fa-store-slash"></i> No outlets yet
                      </div>
                    )}
                  </td>

                  {/* Hours */}
                  <td style={{ ...pbTd, textAlign: "center" }}>
                    {healthcare.openHours ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: "#E6F4EA", color: "#107C41", border: "1px solid #B7DFC5",
                        borderRadius: 12, padding: "3px 10px", fontSize: "0.7rem", fontWeight: 700,
                      }}>
                        <FaClock style={{ fontSize: 10 }} /> Set
                      </span>
                    ) : (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: "#FFF8E1", color: "#D4930A", border: "1px solid #F7D87C",
                        borderRadius: 12, padding: "3px 10px", fontSize: "0.7rem", fontWeight: 700,
                      }}>
                        <FaClock style={{ fontSize: 10 }} /> Not Set
                      </span>
                    )}
                  </td>

                  {/* AI */}
                  <td style={{ ...pbTd, textAlign: "center" }}>
                    <button
                      onClick={() => handleToggleAIShoppable(healthcare._id, healthcare.name, healthcare.isAIShoppable)}
                      disabled={!canManageHealthcare || !!aiToggleLoading[healthcare._id]}
                      title={healthcare.isAIShoppable ? "Disable AI Commerce" : "Enable AI Commerce"}
                      style={{
                        background: "none", border: "none", cursor: (!canManageHealthcare || aiToggleLoading[healthcare._id]) ? "not-allowed" : "pointer",
                        padding: 0,
                      }}
                    >
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "3px 10px", borderRadius: 12, fontSize: "0.7rem", fontWeight: 700,
                        ...(healthcare.isAIShoppable
                          ? { background: "#EBF0FF", color: "#8764B8", border: "1px solid #C5B8E8" }
                          : { background: "#F4F6F9", color: "#9AACBF", border: "1px solid #E4E8EF" })
                      }}>
                        {healthcare.isAIShoppable ? "🤖 On" : "AI Off"}
                      </span>
                    </button>
                  </td>

                  {/* ID */}
                  <td style={{ ...pbTd, fontFamily: "monospace", fontSize: "0.75rem", color: "#9AACBF" }}>
                    <span title={healthcare._id}>
                      {healthcare._id ? healthcare._id.slice(0, 8) + "…" : "—"}
                    </span>
                  </td>

                  {/* Actions */}
                  <td style={{ ...pbTd, textAlign: "center" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 2, background: "#F4F6F9", borderRadius: 6, padding: "3px 4px" }}>
                      <PbActionBtn onClick={() => handleEditClick(healthcare)} disabled={!canUpdateHealthcare} title="Edit vendor">
                        <FaEdit />
                      </PbActionBtn>
                      <span style={{ width: 1, height: 16, background: "#E0E6EF", display: "inline-block", margin: "0 2px" }} />
                      <PbActionBtn onClick={() => handleManageHours(healthcare)} disabled={!canUpdateHealthcare} title="Manage hours">
                        <FaClock />
                      </PbActionBtn>
                      <span style={{ width: 1, height: 16, background: "#E0E6EF", display: "inline-block", margin: "0 2px" }} />
                      <PbActionBtn onClick={() => handleAttachOutletClick(healthcare)} disabled={!canCreateHealthcare} title="Add outlet">
                        <FaCodeBranch />
                      </PbActionBtn>
                      {/* Super-admin only: mail the vendor their login email +
                          a single-use set-password link. Never a password. */}
                      <SendVendorCredentialsAction
                        vendorType="HEALTHCARE"
                        vendorId={healthcare._id}
                        vendorName={healthcare.name}
                        visible={isSuperAdmin}
                      >
                        {({ open, busy }) => (
                          <>
                            <span style={{ width: 1, height: 16, background: "#E0E6EF", display: "inline-block", margin: "0 2px" }} />
                            <PbActionBtn onClick={open} disabled={busy} title="Send login details">
                              <FaKey />
                            </PbActionBtn>
                          </>
                        )}
                      </SendVendorCredentialsAction>
                      <SignInAsVendorButton
                        ownerId={healthcare.owner?._id || healthcare.owner}
                        vendorId={healthcare._id}
                        vendorName={healthcare.name}
                      >
                        {({ open, busy }) => (
                          <PbActionBtn onClick={open} disabled={busy} title="Sign in as vendor (audited, 60 minutes)">
                            <FaUserSecret />
                          </PbActionBtn>
                        )}
                      </SignInAsVendorButton>
                      <span style={{ width: 1, height: 16, background: "#E0E6EF", display: "inline-block", margin: "0 2px" }} />
                      <PbActionBtn onClick={() => handleDeleteHealthcare(healthcare._id)} disabled={!canDeleteHealthcare} title="Delete vendor" danger>
                        <FaTrash />
                      </PbActionBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <div style={{ padding: "24px", background: "#F3F4F6", minHeight: "100vh" }}>
      {/* ── Power BI Page Header ── */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: "0.72rem", color: "#9AACBF", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: 2 }}>
              Healthcare
            </div>
            <h2 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 700, color: "#1B2A3B", display: "flex", alignItems: "center", gap: 10 }}>
              <FaBriefcaseMedical style={{ color: "#118DFF", fontSize: "1.1rem" }} />
              Healthcare Vendors
            </h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {canCreateHealthcare && (
              <button
                onClick={() => history.push({
                  pathname: "/vendor-register",
                  search: "?type=HEALTHCARE",
                  state: { returnTo: "/admin/healthcare-vendors" },
                })}
                title="Create healthcare vendor"
                style={{
                  background: "#118DFF", color: "#fff", border: "1px solid #118DFF",
                  borderRadius: "6px", padding: "7px 14px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                  fontSize: "0.8rem", fontWeight: 700,
                  boxShadow: "0 1px 3px rgba(17,141,255,0.25)",
                }}
              >
                <FaPlus /> Add Vendor
              </button>
            )}
            <button
              onClick={handleRetry}
              title="Refresh data"
              style={{
                background: "#fff", border: "1px solid #E0E6EF", borderRadius: "6px",
                padding: "7px 14px", cursor: "pointer", display: "flex", alignItems: "center",
                gap: 6, fontSize: "0.8rem", fontWeight: 600, color: "#5C6B7A",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              ⟳ Refresh
            </button>
          </div>
        </div>
        <div style={{ height: "3px", background: "linear-gradient(90deg,#118DFF 0%,#8764B8 100%)", borderRadius: 2, marginTop: 14 }} />
      </div>

      <div style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", padding: "24px" }}>
        {editingHealthcare ? (
          <EditHealthcareForm
            healthcare={editingHealthcare}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          content
        )}
      </div>
      <Modal show={showDeleteModal} onHide={toggleDeleteModal} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #C00000", background: '#fafbfc' }}>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.88rem", color: "#3D4F6B", padding: "20px 24px" }}>
          <div style={{ background: "#FDE7E9", border: "1px solid #F5C6CB", borderRadius: 6, padding: "12px 14px" }}>
            ⚠️ Are you sure you want to delete this healthcare vendor? This action can't be undone!
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #E8ECF2", gap: 8 }}>
          <Button color="danger" onClick={confirmDeleteHealthcare} disabled={isSubmitting}>
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
          <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAttachOutletModal} onHide={toggleAttachOutletModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #118DFF", background: '#fafbfc' }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaCodeBranch style={{ color: "#5BC5F5" }} /> Add Outlet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="form-group">
              <label>Outlet Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={outletDetails.name}
                onChange={handleOutletDetailsChange}
                placeholder="Enter outlet name"
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <LocationAutocomplete loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
                value={value}
                setValue={setValue}
                ready={ready}
                data={data}
                clearSuggestions={clearSuggestions}
                mapsApiLoaded={mapsApiLoaded}
                onLocationSelect={handleLocationChange}
              />
            </div>
            <div className="form-group">
              <label>Delivery Zone Radius (km)</label>
              <input
                type="number"
                className="form-control"
                name="deliveryZoneRadius"
                value={outletDetails.deliveryZoneRadius}
                onChange={handleOutletDetailsChange}
                placeholder="Enter delivery zone radius"
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="primary"
            onClick={handleAttachOutlet}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Outlet"}
          </Button>
          <Button color="secondary" onClick={toggleAttachOutletModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteOutletModal} onHide={() => setShowDeleteOutletModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Outlet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this outlet? This action can't be undone!
        </Modal.Body>
        <Modal.Footer>
          <Button color="danger" onClick={confirmDeleteOutlet}>
            Delete
          </Button>
          <Button color="secondary" onClick={() => setShowDeleteOutletModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditOutletModal} onHide={() => setShowEditOutletModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #118DFF", background: '#fafbfc' }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaEdit style={{ color: "#5BC5F5" }} /> Edit Outlet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingOutlet && (
            <form>
              <div className="form-group">
                <label>Outlet Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingOutlet.name || ""}
                  onChange={(e) =>
                    setEditingOutlet({ ...editingOutlet, name: e.target.value })
                  }
                  placeholder="Enter outlet name"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingOutlet.address || ""}
                  onChange={(e) =>
                    setEditingOutlet({ ...editingOutlet, address: e.target.value })
                  }
                  placeholder="Enter outlet address"
                />
              </div>
              <div className="form-group">
                <label>Delivery Zone Radius (km)</label>
                <input
                  type="number"
                  className="form-control"
                  value={editingOutlet.deliveryZoneRadius || 5}
                  onChange={(e) =>
                    setEditingOutlet({
                      ...editingOutlet,
                      deliveryZoneRadius: parseFloat(e.target.value),
                    })
                  }
                  placeholder="Enter delivery zone radius"
                />
              </div>
            </form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="primary"
            onClick={saveOutletChanges}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            color="secondary"
            onClick={() => setShowEditOutletModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Outlet Business Hours Modal ── */}
      <Modal
        show={showOutletHoursModal}
        onHide={() => { setShowOutletHoursModal(false); setSelectedOutletForHours(null); setOutletHours(null); }}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ borderBottom: "2px solid #17a2b8", background: "#fafbfc" }}
        >
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaClock style={{ color: "#083860" }} />
            Outlet Hours: {selectedOutletForHours?.outlet?.name}
            <span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 400 }}>
              ({selectedOutletForHours?.healthcare?.name})
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <div style={{ background: "#e8f5e9", border: "1.5px solid #c8e6c9", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: "0.85rem" }}>
            <strong style={{ color: "#2e7d32" }}>📅 Outlet Operating Hours</strong>
            <p style={{ margin: "6px 0 0", color: "#4a5568" }}>
              Custom hours for <strong>{selectedOutletForHours?.outlet?.name}</strong>. If not set, the outlet inherits the parent vendor's hours.
            </p>
          </div>
          {isLoadingOutletHours ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <span className="spinner-border spinner-border-sm" role="status" />
              <p style={{ marginTop: 10, color: "#9aacbf", fontSize: "0.85rem" }}>Loading outlet hours…</p>
            </div>
          ) : (
            <OpenHoursManager
              vendorLabel="pharmacy"
              // No tradingPolicy here: an outlet with no hours of its own
              // inherits the parent pharmacy's schedule server-side.
              openHours={outletHours}
              timezone={
                selectedOutletForHours?.healthcare?.timezone || "Africa/Kampala"
              }
              // An outlet reads in its parent vendor's zone.
              allowTimezoneEdit={false}
              onSave={handleSaveOutletHours}
              isSaving={isSavingOutletHours}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showHoursModal} onHide={() => setShowHoursModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #118DFF", background: '#fafbfc' }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaClock style={{ color: "#5BC5F5" }} /> Hours — {selectedHealthcareForHours?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingHours ? (
            <div>Loading hours...</div>
          ) : (
            <OpenHoursManager
              vendorLabel="pharmacy"
              // Pharmacies trade 24/7 until hours are saved.
              tradingPolicy="open_until_set"
              vendorType="healthcare"
              vendorId={selectedHealthcareForHours?._id}
              openHours={healthcareHours}
              timezone={selectedHealthcareForHours?.timezone || "Africa/Kampala"}
              onSave={handleSaveHealthcareHours}
              isSaving={isSavingHours}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showOutletConfirmModal}
        onHide={() => {
          setShowOutletConfirmModal(false);
          setOutletToConfirm(null);
          setConfirmAction(null);
          setHealthcareForConfirm(null);
        }}
        centered
        size="lg"
        contentClassName="bg-dark text-white"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="border-secondary"
        >
          <Modal.Title>
            {confirmAction === "activate"
              ? "Confirm Outlet Activation"
              : confirmAction === "deactivate"
              ? "Confirm Outlet Deactivation"
              : "Confirm Delete Outlet"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {confirmAction || "delete"} the outlet{" "}
            <strong>"{outletToConfirm?.name}"</strong>?
          </p>
          <div
            style={{
              backgroundColor: confirmAction === "activate" ? "#d4edda" : "#fff3cd",
              border: `1px solid ${confirmAction === "activate" ? "#28a745" : "#ffc107"}`,
              borderRadius: "8px",
              padding: "12px",
              marginTop: "15px",
            }}
          >
            <small style={{ color: confirmAction === "activate" ? "#155724" : "#856404" }}>
              {confirmAction === "activate"
                ? "✅ Activating this outlet will make it available for customers to place orders."
                : "⚠️ Deactivating this outlet will make it unavailable for customers to place orders!"}
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-secondary">
          <Button
            variant="outline-light"
            onClick={() => {
              setShowOutletConfirmModal(false);
              setOutletToConfirm(null);
              setConfirmAction(null);
              setHealthcareForConfirm(null);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant={confirmAction === "activate" ? "success" : "danger"}
            onClick={confirmAction ? confirmToggleOutlet : confirmDeleteOutlet}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {confirmAction === "activate" ? "Activating..." : "Deactivating..."}
              </>
            ) : confirmAction === "activate" ? (
              "Activate Outlet"
            ) : (
              "Deactivate Outlet"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── AI Commerce Toggle Confirmation Modal ── */}
      <Modal
        show={showAIModal}
        onHide={() => { setShowAIModal(false); setAiModalTarget(null); }}
        centered
        size="sm"
        contentClassName="bg-dark text-white"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ borderBottom: `2px solid ${aiModalTarget?.currentValue ? '#dc3545' : '#117a44'}` }}
        >
          <Modal.Title>
            {aiModalTarget?.currentValue ? '🤖 Disable AI Commerce' : '🤖 Enable AI Commerce'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          <p style={{ fontSize: '0.88rem', marginBottom: 10 }}>
            {aiModalTarget?.currentValue
              ? <>AI agents will <strong>no longer</strong> be able to access <strong>{aiModalTarget?.name}</strong>.</>
              : <><strong>{aiModalTarget?.name}</strong> will become discoverable and orderable by AI agents (ChatGPT, Gemini, etc.).</>}
          </p>
          <div style={{
            background: aiModalTarget?.currentValue ? '#fff3cd' : '#e8f5e9',
            border: `1px solid ${aiModalTarget?.currentValue ? '#ffc107' : '#c8e6c9'}`,
            borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem',
            color: aiModalTarget?.currentValue ? '#856404' : '#2e7d32',
          }}>
            {aiModalTarget?.currentValue
              ? '⚠️ This will immediately remove the vendor from the AI Commerce Gateway.'
              : '✅ This will immediately expose the vendor to the AI Commerce Gateway.'}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-secondary" style={{ gap: 8 }}>
          <Button
            variant="outline-light"
            onClick={() => { setShowAIModal(false); setAiModalTarget(null); }}
          >
            Cancel
          </Button>
          <Button
            variant={aiModalTarget?.currentValue ? 'danger' : 'success'}
            onClick={confirmToggleAI}
          >
            {aiModalTarget?.currentValue ? 'Disable AI' : 'Enable AI'}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Healthcares;
